import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { authedFetch, postJson, putJson } from "../lib/api";
import {
  DEFAULT_FILTERS,
  SORT_LABELS,
  dateBounds,
  filterMovies,
  orphanNotice,
  stars,
  theaterOptions,
  type HistoryBody,
  type HistoryFilters,
  type LoggedMovie,
  type SortOption,
  type TheaterProgress,
  type TrackerMember,
  type WriteResult,
} from "../lib/trackerPage";

// The Movie Tracker: the `Movies Seen` sheet, which is this app's record of
// what was actually watched.
//
// What Streamlit made hard and this fixes: the whole page re-ran on every
// keystroke in the search box, so filtering a few hundred films meant a round
// trip per character. The history is fetched once and every control here is
// array work. The add and edit forms were `st.form`s whose fields could not
// react to each other at all, which is why the poster lookup needed its own
// button outside the form.

type MemberStats = {
  key: string;
  name: string;
  has_data: boolean;
  movies_seen?: number;
  months_active?: number;
  amount_paid?: number;
  total_cost?: number;
  total_value?: number;
  cost_per_movie?: number;
  avg_per_month?: number;
  weekly_movies?: number;
  annual_movies?: number;
  by_theater?: { theater_short: string; count: number }[];
  by_month?: { month: string; count: number }[];
};

type StatsBody = {
  available: boolean;
  members: MemberStats[];
  rating: { average: number | null; count: number } | null;
  letterboxd: {
    available: boolean;
    letterboxd_count: number | null;
    difference: number | null;
    in_sync: boolean;
    member: string | null;
  } | null;
  year: number | null;
};

type UnloggedPlan = {
  date: string;
  date_label: string;
  time: string;
  movie_title: string;
  theater: string;
  theater_short: string;
  auditorium: string;
  seats: string;
  price: number | null;
};

type TmdbLookup = {
  found: boolean;
  retryable?: boolean;
  reason?: string;
  poster_url?: string;
  tmdb_id?: string;
  matched_title?: string;
  release_year?: string;
};

function tmdbLookupPath(title: string, releaseYear: string): string {
  const year = releaseYear.trim();
  return (
    `/api/tracker/poster?title=${encodeURIComponent(title)}` +
    (year ? `&year=${encodeURIComponent(year)}` : "")
  );
}

async function fetchTmdbLookup(title: string, releaseYear: string): Promise<TmdbLookup> {
  const res = await authedFetch(tmdbLookupPath(title, releaseYear));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TmdbLookup;
}

type MovieForm = {
  movie_title: string;
  date: string;
  theater: string;
  auditorium: string;
  seats: string;
  cost: string;
  rating: number;
  alist_members: string[];
  notes: string;
};

const TABS = ["history", "theaters", "stats"] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  history: "📊 History",
  theaters: "🎭 Theaters",
  stats: "📈 Stats",
};

const field =
  "rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm";
const button = "rounded-md border border-neutral-700 px-3 py-1.5 text-sm disabled:opacity-40";

function Stars({ rating }: { rating: number | null }) {
  const { full, half, empty } = stars(rating);
  if (!rating) return null;
  return (
    <span className="text-[#DAA520]" aria-label={`${rating} out of 5`}>
      {"★".repeat(full)}
      {half ? "½" : ""}
      {"☆".repeat(empty)}
    </span>
  );
}

function Poster({ url, size }: { url: string; size: "sm" | "lg" }) {
  const box = size === "lg" ? "h-[150px] w-[100px]" : "h-[90px] w-[60px]";
  if (!url) {
    return (
      <div
        className={`${box} shrink-0 rounded bg-neutral-900 text-center text-2xl leading-[150px] text-neutral-700`}
      >
        🎬
      </div>
    );
  }
  return <img src={url} alt="" className={`${box} shrink-0 rounded object-cover`} />;
}

/** The fields shared by the add and edit forms. */
function MovieFields({
  value,
  onChange,
  theaters,
  members,
}: {
  value: MovieForm;
  onChange: (next: MovieForm) => void;
  theaters: string[];
  members: TrackerMember[];
}) {
  const set = <K extends keyof MovieForm>(key: K, next: MovieForm[K]) =>
    onChange({ ...value, [key]: next });

  const toggleMember = (key: string) =>
    set(
      "alist_members",
      value.alist_members.includes(key)
        ? value.alist_members.filter((k) => k !== key)
        : [...value.alist_members, key],
    );

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="grid gap-1 text-xs text-neutral-400">
        Theater*
        <select
          value={value.theater}
          onChange={(e) => set("theater", e.target.value)}
          className={field}
        >
          {/* The current value is offered even when it is not in the list: a
              theater dropped from the config still has films logged against
              it, and an option that silently vanishes would move the film to
              whatever the picker defaulted to on the next save. */}
          {!theaters.includes(value.theater) && value.theater && (
            <option value={value.theater}>{value.theater}</option>
          )}
          {theaters.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>

      <label className="grid gap-1 text-xs text-neutral-400">
        Date watched*
        <input
          type="date"
          value={value.date}
          onChange={(e) => set("date", e.target.value)}
          className={field}
        />
      </label>

      <label className="grid gap-1 text-xs text-neutral-400">
        Auditorium*
        <input
          value={value.auditorium}
          onChange={(e) => set("auditorium", e.target.value)}
          placeholder="e.g. 5"
          className={field}
        />
      </label>

      <label className="grid gap-1 text-xs text-neutral-400">
        Seats*
        <input
          value={value.seats}
          onChange={(e) => set("seats", e.target.value)}
          placeholder="e.g. F12, F13"
          className={field}
        />
      </label>

      <label className="grid gap-1 text-xs text-neutral-400">
        Cost ($)*
        <input
          type="number"
          min="0"
          step="0.01"
          value={value.cost}
          onChange={(e) => set("cost", e.target.value)}
          className={field}
        />
      </label>

      <fieldset className="grid gap-1 text-xs text-neutral-400">
        <legend>A-List</legend>
        <div className="flex gap-3 pt-1">
          {members.map((member) => (
            <label key={member.key} className="flex items-center gap-1.5 text-sm text-neutral-300">
              <input
                type="checkbox"
                checked={value.alist_members.includes(member.key)}
                onChange={() => toggleMember(member.key)}
              />
              {member.name}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="grid gap-1 text-xs text-neutral-400 sm:col-span-2">
        <legend>Rating*</legend>
        <div className="flex gap-1 pt-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
              aria-pressed={value.rating === n}
              onClick={() => set("rating", n)}
              className={
                "px-1 text-xl " +
                (n <= value.rating ? "text-[#DAA520]" : "text-neutral-700")
              }
            >
              ★
            </button>
          ))}
        </div>
      </fieldset>

      <label className="grid gap-1 text-xs text-neutral-400 sm:col-span-2">
        Notes
        <textarea
          value={value.notes}
          onChange={(e) => set("notes", e.target.value)}
          rows={2}
          className={field}
        />
      </label>
    </div>
  );
}

function AddMovie({
  theaters,
  members,
  plans,
  onDone,
  onCancel,
  onError,
}: {
  theaters: string[];
  members: TrackerMember[];
  plans: UnloggedPlan[];
  onDone: (result: WriteResult, title: string) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const today = new Date();
  const [form, setForm] = useState<MovieForm>({
    movie_title: "",
    date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`,
    theater: theaters[0] ?? "",
    auditorium: "",
    seats: "",
    cost: "",
    rating: 0,
    alist_members: members.length ? [members[0].key] : [],
    notes: "",
  });
  const [planIndex, setPlanIndex] = useState(-1);
  const [poster, setPoster] = useState({ url: "", tmdb_id: "" });
  const [pendingMatch, setPendingMatch] = useState<{
    url: string;
    tmdb_id: string;
    matched_title: string;
    release_year: string;
  } | null>(null);
  const [releaseYear, setReleaseYear] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const lookupSequence = useRef(0);
  // OFF until the diary write routes through throw-rag. amc-movies no longer
  // owns the Letterboxd credential, and `add_movie_and_log` calls Letterboxd
  // BEFORE the sheet write -- so a failure here loses the row entirely, not
  // just the diary entry. Flip it back to `true` in the same change that moves
  // the diary write to the hub.
  const [logToLetterboxd, setLogToLetterboxd] = useState(false);
  const [busy, setBusy] = useState(false);

  const lookupPoster = useCallback(
    async (title: string, year: string) => {
      const sequence = ++lookupSequence.current;
      if (!title.trim()) {
        setPoster({ url: "", tmdb_id: "" });
        setPendingMatch(null);
        setLookupError("");
        return;
      }
      setLookingUp(true);
      setLookupError("");
      try {
        const body = await fetchTmdbLookup(title, year);
        if (sequence !== lookupSequence.current) return;
        setPoster({ url: "", tmdb_id: "" });
        setPendingMatch(body.found ? {
          url: body.poster_url ?? "",
          tmdb_id: body.tmdb_id ?? "",
          matched_title: body.matched_title ?? title,
          release_year: body.release_year ?? year,
        } : null);
        if (!body.found && body.retryable) {
          setLookupError(body.reason ?? "TMDb is unavailable right now.");
        }
      } catch {
        if (sequence !== lookupSequence.current) return;
        setPoster({ url: "", tmdb_id: "" });
        setPendingMatch(null);
        setLookupError("TMDb is unavailable right now.");
      } finally {
        if (sequence === lookupSequence.current) setLookingUp(false);
      }
    },
    [],
  );

  const choosePlan = (index: number) => {
    setPlanIndex(index);
    if (index < 0) return;
    const plan = plans[index];
    setForm((prev) => ({
      ...prev,
      movie_title: plan.movie_title,
      date: plan.date,
      theater: plan.theater_short,
      auditorium: plan.auditorium,
      seats: plan.seats,
      cost: plan.price != null ? String(plan.price) : prev.cost,
      // Two seats means two people, so both memberships were spent. The
      // Streamlit form did the same and it is right far more often than not.
      alist_members: plan.seats.includes(",")
        ? members.map((m) => m.key)
        : prev.alist_members,
    }));
    setReleaseYear("");
    setPoster({ url: "", tmdb_id: "" });
    setPendingMatch(null);
    void lookupPoster(plan.movie_title, "");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const result = await postJson<WriteResult>("/api/tracker/movies", {
        ...form,
        cost: Number(form.cost),
        poster_url: poster.url,
        tmdb_id: poster.tmdb_id,
        log_to_letterboxd: logToLetterboxd && !!poster.tmdb_id,
        plan: planIndex >= 0 ? plans[planIndex] : null,
      });
      onDone(result, form.movie_title);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
      <h2 className="text-lg font-semibold">Add a movie</h2>

      {plans.length > 0 && (
        <label className="grid gap-1 text-xs text-neutral-400">
          From the planner
          <select
            value={planIndex}
            onChange={(e) => choosePlan(Number(e.target.value))}
            className={field}
          >
            <option value={-1}>-- enter manually --</option>
            {plans.map((plan, index) => (
              <option key={`${plan.date}|${plan.time}|${plan.movie_title}`} value={index}>
                {plan.movie_title} · {plan.date_label} · {plan.theater_short}
                {plan.price != null ? ` · $${plan.price.toFixed(2)}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="flex gap-3">
        <Poster url={pendingMatch?.url ?? poster.url} size="lg" />
        <div className="flex-1 space-y-3">
          <label className="grid gap-1 text-xs text-neutral-400">
            Movie title*
            <input
              value={form.movie_title}
              onChange={(e) => {
                lookupSequence.current += 1;
                setPoster({ url: "", tmdb_id: "" });
                setPendingMatch(null);
                setLookupError("");
                setLookingUp(false);
                setForm({ ...form, movie_title: e.target.value });
              }}
              onBlur={(e) => void lookupPoster(e.target.value, releaseYear)}
              placeholder="e.g. Dune: Part Two"
              className={field}
            />
          </label>
          <label className="grid gap-1 text-xs text-neutral-400">
            Release year (optional)
            <input
              type="number"
              min="1900"
              max="2100"
              value={releaseYear}
              onChange={(e) => {
                lookupSequence.current += 1;
                setPoster({ url: "", tmdb_id: "" });
                setPendingMatch(null);
                setLookupError("");
                setLookingUp(false);
                setReleaseYear(e.target.value);
              }}
              onBlur={() => void lookupPoster(form.movie_title, releaseYear)}
              placeholder="e.g. 1982"
              className={field}
            />
          </label>
          <p className="text-xs text-neutral-600">
            TMDb uses the release year only when you provide one. The date watched
            is never used as the movie's release year.
          </p>
          {lookingUp && <p className="text-xs text-neutral-500">Looking up TMDb…</p>}
          {pendingMatch && (
            <div className="space-y-2 rounded border border-neutral-700 p-2 text-xs">
              <p>
                Match: {pendingMatch.matched_title}
                {pendingMatch.release_year ? ` (${pendingMatch.release_year})` : ""}
              </p>
              <button
                type="button"
                onClick={() => {
                  setPoster({ url: pendingMatch.url, tmdb_id: pendingMatch.tmdb_id });
                  setPendingMatch(null);
                }}
                className={button}
              >
                Use this TMDb match
              </button>
            </div>
          )}
          {lookupError && (
            <div role="alert" className="flex items-center gap-2 text-xs text-amber-400">
              <span>{lookupError}</span>
              <button
                type="button"
                onClick={() => void lookupPoster(form.movie_title, releaseYear)}
                className={button}
              >
                Retry TMDb
              </button>
            </div>
          )}
        </div>
      </div>

      <MovieFields value={form} onChange={setForm} theaters={theaters} members={members} />

      <label className="flex items-center gap-2 text-sm text-neutral-300">
        <input
          type="checkbox"
          checked={logToLetterboxd && !!poster.tmdb_id}
          disabled={!poster.tmdb_id}
          onChange={(e) => setLogToLetterboxd(e.target.checked)}
        />
        Log to Letterboxd
        {!poster.tmdb_id && (
          <span className="text-xs text-neutral-500">
            (needs TMDb match)
          </span>
        )}
      </label>

      <div className="flex gap-2">
        <button onClick={() => void submit()} disabled={busy} className={button}>
          Save
        </button>
        <button onClick={onCancel} disabled={busy} className={button}>
          Cancel
        </button>
      </div>
    </section>
  );
}

function EditMovie({
  movie,
  theaters,
  members,
  onSaved,
  onDeleted,
  onCancel,
  onError,
}: {
  movie: LoggedMovie;
  theaters: string[];
  members: TrackerMember[];
  onSaved: () => void;
  onDeleted: (result: WriteResult, title: string) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState<MovieForm>({
    movie_title: movie.movie_title,
    date: movie.date,
    // The RAW theater, not the short one: this is what goes back to the sheet,
    // and normalising it here would silently rewrite the cell on an edit that
    // never touched the theater.
    theater: movie.theater,
    auditorium: movie.auditorium,
    seats: movie.seats,
    cost: String(movie.cost),
    rating: movie.rating ?? 0,
    alist_members: movie.alist_members,
    notes: movie.notes,
  });
  const [confirming, setConfirming] = useState(false);
  const [synced, setSynced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tmdbBusy, setTmdbBusy] = useState(false);
  const [tmdbId, setTmdbId] = useState(movie.tmdb_id);
  const [releaseYear, setReleaseYear] = useState("");
  const [tmdbCandidate, setTmdbCandidate] = useState<TmdbLookup | null>(null);
  const [tmdbError, setTmdbError] = useState("");
  const [savedMatch, setSavedMatch] = useState("");
  const editLookupSequence = useRef(0);

  const canSyncLetterboxd = !!tmdbId && !movie.letterboxd_id && !synced;

  const findTmdbMatch = async () => {
    const sequence = ++editLookupSequence.current;
    setTmdbBusy(true);
    setTmdbError("");
    setTmdbCandidate(null);
    try {
      const body = await fetchTmdbLookup(form.movie_title, releaseYear);
      if (sequence !== editLookupSequence.current) return;
      if (body.found && body.tmdb_id) {
        setTmdbCandidate(body);
      } else {
        setTmdbError(
          body.retryable
            ? body.reason ?? "TMDb is unavailable right now. Retry the lookup."
            : body.reason ?? "No TMDb match found.",
        );
      }
    } catch {
      if (sequence !== editLookupSequence.current) return;
      setTmdbError("TMDb is unavailable right now. Retry the lookup.");
    } finally {
      if (sequence === editLookupSequence.current) setTmdbBusy(false);
    }
  };

  const saveTmdbMatch = async () => {
    if (!tmdbCandidate?.tmdb_id) return;
    setTmdbBusy(true);
    setTmdbError("");
    try {
      const res = await authedFetch(`/api/tracker/movies/${movie.id}/tmdb`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tmdb_id: tmdbCandidate.tmdb_id,
          poster_url: tmdbCandidate.poster_url ?? "",
        }),
      });
      const body = (await res.json()) as { detail?: string };
      if (!res.ok) {
        setTmdbError(body.detail ?? "Could not save the TMDb match.");
        return;
      }
      setTmdbId(tmdbCandidate.tmdb_id);
      setSavedMatch(
        `${tmdbCandidate.matched_title ?? form.movie_title}` +
          (tmdbCandidate.release_year ? ` (${tmdbCandidate.release_year})` : ""),
      );
      setTmdbCandidate(null);
    } catch {
      setTmdbError("Could not save the TMDb match.");
    } finally {
      setTmdbBusy(false);
    }
  };

  const syncLetterboxd = async () => {
    setBusy(true);
    try {
      const res = await authedFetch(`/api/tracker/movies/${movie.id}/letterboxd`, {
        method: "POST",
      });
      const body = (await res.json()) as WriteResult;
      if (body.letterboxd_orphaned) {
        setSynced(true);
        onError(orphanNotice(body));
        return;
      }
      if (!res.ok) {
        const detail = (body as unknown as { detail?: string }).detail ?? "Sync failed";
        onError(detail);
        return;
      }
      setSynced(true);
      onSaved();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await putJson(`/api/tracker/movies/${movie.id}`, {
        ...form,
        cost: Number(form.cost),
      });
      onSaved();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const res = await authedFetch(`/api/tracker/movies/${movie.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Delete failed (HTTP ${res.status})`);
      onDeleted((await res.json()) as WriteResult, movie.movie_title);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="space-y-3 rounded-lg border border-neutral-700 bg-neutral-900/60 p-4">
      <h3 className="font-semibold">Editing: {movie.movie_title}</h3>
      <label className="grid gap-1 text-xs text-neutral-400">
        Movie title*
        <input
          value={form.movie_title}
          onChange={(e) => {
            editLookupSequence.current += 1;
            setTmdbBusy(false);
            setTmdbCandidate(null);
            setTmdbError("");
            setForm({ ...form, movie_title: e.target.value });
          }}
          className={field}
        />
      </label>
      <MovieFields value={form} onChange={setForm} theaters={theaters} members={members} />

      {!tmdbId && !movie.letterboxd_id && (
        <section className="space-y-2 rounded-md border border-neutral-800 p-3">
          <p className="text-sm text-neutral-300">
            This row has no TMDb ID. Find the movie, confirm the match, then you can
            sync it to Letterboxd.
          </p>
          <label className="grid max-w-48 gap-1 text-xs text-neutral-400">
            Release year (optional)
            <input
              type="number"
              min="1900"
              max="2100"
              value={releaseYear}
              onChange={(e) => {
                editLookupSequence.current += 1;
                setTmdbBusy(false);
                setTmdbCandidate(null);
                setTmdbError("");
                setReleaseYear(e.target.value);
              }}
              className={field}
            />
          </label>
          <button
            onClick={() => void findTmdbMatch()}
            disabled={busy || tmdbBusy}
            className={button}
          >
            Find TMDb match
          </button>
          {tmdbCandidate?.tmdb_id && (
            <div role="status" className="space-y-2 text-sm text-neutral-300">
              <p>
                Match: {tmdbCandidate.matched_title ?? form.movie_title}
                {tmdbCandidate.release_year && ` (${tmdbCandidate.release_year})`}
                {tmdbCandidate.poster_url ? "" : " (no poster available)"}
              </p>
              <button
                onClick={() => void saveTmdbMatch()}
                disabled={busy || tmdbBusy}
                className={`${button} border-green-900 text-green-400`}
              >
                Save this TMDb match
              </button>
            </div>
          )}
          {tmdbError && <p role="alert" className="text-sm text-amber-400">{tmdbError}</p>}
        </section>
      )}

      {savedMatch && (
        <p role="status" className="text-sm text-green-400">
          Saved TMDb match: {savedMatch}. Letterboxd sync is now available.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => void save()} disabled={busy} className={button}>
          Save
        </button>
        <button onClick={onCancel} disabled={busy} className={button}>
          Cancel
        </button>
        {canSyncLetterboxd && (
          <button
            onClick={() => void syncLetterboxd()}
            disabled={busy}
            className={`${button} border-green-900 text-green-400`}
          >
            Sync to Letterboxd
          </button>
        )}
        <button
          onClick={() => setConfirming(true)}
          disabled={busy}
          className={`${button} border-red-900 text-red-400`}
        >
          Delete
        </button>
      </div>

      {confirming && (
        <div role="alert" className="space-y-2 rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          <p>
            Delete <strong>{movie.movie_title}</strong>? This cannot be undone.
            {movie.letterboxd_id && " Its Letterboxd diary entry goes too."}
          </p>
          <div className="flex gap-2">
            <button onClick={() => void remove()} disabled={busy} className={button}>
              Yes, delete
            </button>
            <button onClick={() => setConfirming(false)} disabled={busy} className={button}>
              Keep it
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function MovieRow({ movie, onEdit }: { movie: LoggedMovie; onEdit: () => void }) {
  return (
    <li className="flex gap-3 rounded-lg border border-neutral-800 bg-neutral-900/40 p-3">
      <Poster url={movie.poster_url} size="sm" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline gap-2">
          <h3 className="truncate font-medium text-neutral-200">{movie.movie_title}</h3>
          {movie.letterboxd_url && (
            <a
              href={movie.letterboxd_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-neutral-500 underline hover:text-neutral-300"
            >
              Letterboxd
            </a>
          )}
        </div>
        <p className="text-sm text-neutral-400">
          {movie.date_label} • {movie.theater_short}
          {movie.auditorium && ` • Aud ${movie.auditorium}`}
        </p>
        <p className="text-sm text-neutral-400">
          {movie.seats && <span>Seats: {movie.seats} </span>}
          <Stars rating={movie.rating} />
        </p>
        {movie.notes && <p className="text-sm italic text-neutral-500">{movie.notes}</p>}
      </div>
      <button onClick={onEdit} aria-label={`Edit ${movie.movie_title}`} className={button}>
        ✏️
      </button>
    </li>
  );
}

function HistoryTab({
  body,
  filters,
  setFilters,
  editing,
  setEditing,
  onSaved,
  onDeleted,
  onError,
}: {
  body: HistoryBody;
  filters: HistoryFilters;
  setFilters: (next: HistoryFilters) => void;
  editing: string | null;
  setEditing: (id: string | null) => void;
  onSaved: () => void;
  onDeleted: (result: WriteResult, title: string) => void;
  onError: (message: string) => void;
}) {
  const shown = useMemo(() => filterMovies(body.movies, filters), [body.movies, filters]);
  const theaters = useMemo(
    () => theaterOptions(body.movies, filters.member),
    [body.movies, filters.member],
  );
  const bounds = useMemo(() => dateBounds(body.movies), [body.movies]);
  const set = <K extends keyof HistoryFilters>(key: K, value: HistoryFilters[K]) =>
    setFilters({ ...filters, [key]: value });

  return (
    <div className="space-y-4">
      <input
        value={filters.search}
        onChange={(e) => set("search", e.target.value)}
        placeholder="🔍 Search by title"
        aria-label="Search by title"
        className={`${field} w-full`}
      />

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="grid gap-1 text-xs text-neutral-400">
          A-List
          <select
            value={filters.member}
            onChange={(e) => set("member", e.target.value)}
            className={field}
          >
            {body.members.map((member) => (
              <option key={member.key} value={member.key}>
                {member.name}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-xs text-neutral-400">
          Theater
          <select
            value={filters.theater}
            onChange={(e) => set("theater", e.target.value)}
            className={field}
          >
            <option value="">All</option>
            {theaters.map((theater) => (
              <option key={theater.name} value={theater.name}>
                {theater.name} ({theater.count})
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-xs text-neutral-400">
          Sort by
          <select
            value={filters.sort}
            onChange={(e) => set("sort", e.target.value as SortOption)}
            className={field}
          >
            {Object.entries(SORT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-xs text-neutral-400">
          Min rating
          <select
            value={filters.minRating}
            onChange={(e) => set("minRating", Number(e.target.value))}
            className={field}
          >
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n === 0 ? "All" : "★".repeat(n) + (n < 5 ? "+" : "")}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-xs text-neutral-400">
          From
          <input
            type="date"
            value={filters.from}
            min={bounds.min}
            max={bounds.max}
            onChange={(e) => set("from", e.target.value)}
            className={field}
          />
        </label>

        <label className="grid gap-1 text-xs text-neutral-400">
          To
          <input
            type="date"
            value={filters.to}
            min={bounds.min}
            max={bounds.max}
            onChange={(e) => set("to", e.target.value)}
            className={field}
          />
        </label>
      </div>

      <p className="text-sm text-neutral-400">
        <strong className="text-neutral-200">{shown.length}</strong>{" "}
        {shown.length === 1 ? "movie" : "movies"}
      </p>

      <ul className="space-y-2">
        {shown.map((movie) =>
          editing === movie.id ? (
            <EditMovie
              key={movie.id}
              movie={movie}
              theaters={body.theaters}
              members={body.members}
              onSaved={onSaved}
              onDeleted={onDeleted}
              onCancel={() => setEditing(null)}
              onError={onError}
            />
          ) : (
            <MovieRow key={movie.id} movie={movie} onEdit={() => setEditing(movie.id)} />
          ),
        )}
      </ul>
    </div>
  );
}

function TheatersTab({
  progress,
  members,
  member,
  onMember,
}: {
  progress: TheaterProgress[];
  members: TrackerMember[];
  member: string;
  onMember: (key: string) => void;
}) {
  return (
    <div className="space-y-4">
      <label className="grid max-w-xs gap-1 text-xs text-neutral-400">
        A-List
        <select value={member} onChange={(e) => onMember(e.target.value)} className={field}>
          {members.map((m) => (
            <option key={m.key} value={m.key}>
              {m.name}
            </option>
          ))}
        </select>
      </label>

      {progress.length === 0 && (
        <p className="text-sm text-neutral-500">Nothing logged for this member yet.</p>
      )}

      <ul className="space-y-2">
        {progress.map((theater) => (
          <li
            key={theater.theater_short}
            className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="font-medium text-neutral-200">{theater.theater_short}</h3>
              <span className="text-sm text-neutral-500">
                {theater.seen_count} {theater.seen_count === 1 ? "film" : "films"}
              </span>
            </div>
            {theater.complete && (
              <p className="text-sm text-emerald-400">
                🏆 All {theater.total} auditoriums visited
              </p>
            )}
            {/* `total: null` means the theater's name carries no auditorium
                count, so there is no denominator. Showing "5/0" or hiding the
                theater would both be worse than saying what is known. */}
            {!theater.complete && theater.total != null && (
              <p className="text-sm text-neutral-400">
                {theater.visited.length}/{theater.total} auditoriums · missing{" "}
                {theater.missing.join(", ")}
              </p>
            )}
            {theater.total == null && (
              <p className="text-sm text-neutral-400">
                Auditoriums visited: {theater.visited.join(", ") || "none recorded"}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/40 p-2">
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="text-lg font-semibold text-neutral-200">{value}</dd>
    </div>
  );
}

function StatsTab({ stats }: { stats: StatsBody }) {
  const money = (n?: number) => `$${(n ?? 0).toFixed(2)}`;

  return (
    <div className="space-y-6">
      {stats.rating?.average != null && (
        <p className="text-sm text-neutral-400">
          Average rating <Stars rating={stats.rating.average} />{" "}
          {stats.rating.average.toFixed(1)} over {stats.rating.count} rated{" "}
          {stats.rating.count === 1 ? "film" : "films"}.
        </p>
      )}

      {stats.letterboxd?.available && (
        <p className="text-sm text-neutral-400">
          Letterboxd: {stats.letterboxd.letterboxd_count} films in {stats.year}
          {stats.letterboxd.in_sync
            ? " — in sync."
            : ` (tracker has ${
                (stats.letterboxd.letterboxd_count ?? 0) +
                (stats.letterboxd.difference ?? 0)
              }, difference ${stats.letterboxd.difference})`}
        </p>
      )}

      {stats.members.map((member) => (
        <section key={member.key} className="space-y-3">
          <h2 className="text-lg font-semibold">{member.name}</h2>
          {!member.has_data ? (
            <p className="text-sm text-neutral-500">No A-List films logged yet.</p>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Metric label="Movies seen" value={member.movies_seen ?? 0} />
                <Metric label="Months active" value={member.months_active ?? 0} />
                <Metric label={`Films in ${stats.year}`} value={member.annual_movies ?? 0} />
                <Metric label="Amount paid" value={money(member.amount_paid)} />
                <Metric label="Ticket value" value={money(member.total_cost)} />
                <Metric label="Net value" value={money(member.total_value)} />
                <Metric label="Cost per movie" value={money(member.cost_per_movie)} />
                <Metric
                  label="Films per month"
                  value={(member.avg_per_month ?? 0).toFixed(1)}
                />
                <Metric label="This week" value={member.weekly_movies ?? 0} />
              </dl>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <h3 className="mb-1 text-sm font-medium text-neutral-300">By theater</h3>
                  <ul className="space-y-0.5 text-sm text-neutral-400">
                    {member.by_theater?.map((row) => (
                      <li key={row.theater_short} className="flex justify-between gap-4">
                        <span>{row.theater_short}</span>
                        <span className="text-neutral-500">{row.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-medium text-neutral-300">By month</h3>
                  <ul className="space-y-0.5 text-sm text-neutral-400">
                    {member.by_month?.slice(0, 12).map((row) => (
                      <li key={row.month} className="flex justify-between gap-4">
                        <span>{row.month}</span>
                        <span className="text-neutral-500">{row.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </section>
      ))}
    </div>
  );
}

export function Tracker() {
  const [body, setBody] = useState<HistoryBody | null>(null);
  const [stats, setStats] = useState<StatsBody | null>(null);
  const [plans, setPlans] = useState<UnloggedPlan[]>([]);
  const [tab, setTab] = useState<Tab>("history");
  const [filters, setFilters] = useState<HistoryFilters>({
    member: "",
    ...DEFAULT_FILTERS,
  });
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // One banner at a time, by construction. Held as a pair of setters over a
  // single value rather than two independent `useState`s: nothing ever cleared
  // the other one, so a failed history fetch left its red banner on screen
  // forever -- including underneath the green "Logged X." that followed a
  // perfectly successful write.
  const [banner, setBanner] = useState<{ kind: "error" | "notice"; text: string } | null>(
    null,
  );
  const setError = useCallback(
    (text: string | null) => setBanner(text ? { kind: "error", text } : null),
    [],
  );
  const setNotice = useCallback(
    (text: string | null) => setBanner(text ? { kind: "notice", text } : null),
    [],
  );
  const error = banner?.kind === "error" ? banner.text : null;
  const notice = banner?.kind === "notice" ? banner.text : null;

  /** Reload the history.
   *
   *  `onFailure` exists because this runs immediately after a successful write,
   *  and with one banner a failed reload would otherwise REPLACE "Logged X."
   *  with a bare fetch error -- leaving someone who just logged a film with no
   *  evidence it worked and every reason to log it again. The caller supplies a
   *  message that keeps the write's outcome in it.
   */
  const refresh = useCallback((onFailure?: string) => {
    authedFetch("/api/tracker/history")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<HistoryBody>;
      })
      .then((next) => {
        setBody(next);
        // The member filter defaults to whoever the server lists first, and is
        // set here rather than in useState because the list comes from config
        // the client has not seen at mount.
        setFilters((prev) =>
          prev.member || !next.members.length
            ? prev
            : { ...prev, member: next.members[0].key },
        );
      })
      .catch((err: Error) => setError(onFailure ?? err.message));
    // `setError` is a stable useCallback over `setBanner`, so listing it costs
    // nothing; it is here because it is no longer a bare setState identity the
    // exhaustive-deps rule knows to skip.
  }, [setError]);

  useEffect(() => refresh(), [refresh]);

  // The statistics are a separate fetch because they make a Letterboxd call,
  // and the film list must not wait on a rate-limited third party. Loaded when
  // the tab is first opened rather than up front.
  useEffect(() => {
    if (tab !== "stats" || stats) return;
    // Both fetches below must check `res.ok` before parsing. They used to rely
    // on `res.json()` rejecting to detect failure, which worked only while a
    // failed response carried an unparseable body. `authedFetch` now converts a
    // non-JSON 2xx into a well-formed 502 `{detail}`, so parsing SUCCEEDS on
    // failure and the wrongly-shaped object reaches state: `stats.members` is
    // then undefined and StatsTab's `.map` crashes the render.
    authedFetch("/api/tracker/stats")
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (HTTP ${res.status})`);
        return res.json() as Promise<StatsBody>;
      })
      .then(setStats)
      .catch((err: Error) => setError(err.message));
  }, [tab, stats, setError]);

  useEffect(() => {
    if (!adding) return;
    authedFetch("/api/tracker/plans")
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (HTTP ${res.status})`);
        return res.json() as Promise<{ available: boolean; plans: UnloggedPlan[] }>;
      })
      .then((next) => setPlans(next.plans))
      .catch(() => setPlans([]));
  }, [adding]);

  const afterWrite = useCallback((saved: string) => {
    setStats(null);
    setEditing(null);
    setAdding(false);
    refresh(`${saved} The list could not be reloaded, so it may be out of date.`);
  }, [refresh]);

  /** Report a write that touched Letterboxd, then reload.
   *
   *  `letterboxd_orphaned` is the one outcome that must never be shown as a
   *  plain error: the diary entry EXISTS and this app can no longer see it, so
   *  retrying double-logs. The id is the only handle on the orphan, which is
   *  why it is in the message rather than in a log line.
   */
  const reportWrite = useCallback(
    (result: WriteResult, title: string, verb: string) => {
      if (result.letterboxd_orphaned) {
        setNotice(orphanNotice(result, title));
        return;
      }
      if (!result.ok) {
        setError(
          result.blocked_before_write
            ? `${result.error}. Nothing was saved, so you can try again.`
            : result.error,
        );
        return;
      }
      const saved = `${verb} ${title}.`;
      setNotice(saved);
      afterWrite(saved);
    },
    [afterWrite, setError, setNotice],
  );

  const progress = body?.progress?.[filters.member] ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 pb-16">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/" className="text-sm text-neutral-400 underline hover:text-neutral-200">
          ← Movie Finder
        </Link>
        <h1 className="text-xl font-semibold">🎬 Movie Tracker</h1>
        <button
          onClick={() => {
            setAdding((on) => !on);
            setEditing(null);
          }}
          className={`${button} ml-auto`}
        >
          {adding ? "Close" : "➕ Add movie"}
        </button>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {notice}
        </div>
      )}

      {body && !body.available && (
        <div role="alert" className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {/* The two causes read very differently to whoever sees this.
              "unreadable" self-heals; "malformed" means the sheet was read
              fine and something downstream choked, which is either a cell to
              go and fix or a bug here -- and saying "Sheets is unavailable"
              for that sends someone to check a service that is working. */}
          {body.reason === "malformed"
            ? "Your history was read but could not be interpreted. That usually means a hand-edited cell -- a date, a cost, or a duplicated column heading. Nothing is lost; the sheet still has every row."
            : "Google Sheets is unavailable, so your history could not be read. Nothing is missing, this page just cannot see it right now."}
        </div>
      )}

      {adding && body?.available && (
        <AddMovie
          theaters={body.theaters}
          members={body.members}
          plans={plans}
          onDone={(result, title) => reportWrite(result, title, "Logged")}
          onCancel={() => setAdding(false)}
          onError={setError}
        />
      )}

      {body?.available && (
        <>
          <nav className="flex gap-1 border-b border-neutral-800">
            {TABS.map((name) => (
              <button
                key={name}
                onClick={() => setTab(name)}
                aria-pressed={tab === name}
                className={
                  "px-3 py-2 text-sm " +
                  (tab === name
                    ? "border-b-2 border-neutral-300 text-neutral-100"
                    : "text-neutral-500 hover:text-neutral-300")
                }
              >
                {TAB_LABELS[name]}
              </button>
            ))}
          </nav>

          {body.movies.length === 0 ? (
            <p className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 text-sm text-neutral-400">
              Nothing logged yet. Use “Add movie” after your next screening.
            </p>
          ) : tab === "history" ? (
            <HistoryTab
              body={body}
              filters={filters}
              setFilters={setFilters}
              editing={editing}
              setEditing={setEditing}
              onSaved={() => {
                setNotice("Saved.");
                afterWrite("Saved.");
              }}
              onDeleted={(result, title) => reportWrite(result, title, "Deleted")}
              onError={setError}
            />
          ) : tab === "theaters" ? (
            <TheatersTab
              progress={progress}
              members={body.members}
              member={filters.member}
              onMember={(key) => setFilters({ ...filters, member: key })}
            />
          ) : stats?.available ? (
            <StatsTab stats={stats} />
          ) : (
            <p className="text-sm text-neutral-500">Loading statistics…</p>
          )}
        </>
      )}

      {!body && !error && <p className="text-sm text-neutral-500">Loading…</p>}
    </div>
  );
}
